import React, { useState, useEffect } from 'react';
import { Typography } from 'antd';
import { SbModal, SbForm, SbButton } from '../../../../uiCore';
import CoreMarkdownEditor from '../../../../Components/SbMarkdownEditor/CoreMarkdownEditor';
import { VIEWMODE } from '../../../../constants/markdown.constants';
import {
  ModalHeaderText,
  ModalDescription,
  FormatGuideBox,
  CharacterCounter
} from '../../AdminSetup.styles';

const { Text } = Typography;

const CompanyContextModal = ({ visible, onClose, initialData, onSave, cardKey }) => {
  const [form] = SbForm.useForm();
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [editorKey, setEditorKey] = useState(0);

  // Convert object to rich text dynamically
  const objectToRichText = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    
    let richText = '';
    
    Object.entries(obj).forEach(([key, value]) => {
      // Convert snake_case to Title Case for headings
      const heading = key.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      richText += `## ${heading}\n\n`;
      
      if (Array.isArray(value)) {
        value.forEach(item => {
          richText += `• ${item}\n`;
        });
        richText += '\n';
      } else if (typeof value === 'object' && value !== null) {
        // Nested object - create sub-headings
        Object.entries(value).forEach(([subKey, subValue]) => {
          const subHeading = subKey.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
          richText += `### ${subHeading}\n${subValue}\n\n`;
        });
      } else {
        richText += `${value}\n\n`;
      }
    });
    
    return richText.trim();
  };

  // Parse rich text back to object dynamically
  const richTextToObject = (text) => {
    if (!text) return {};
    
    const lines = text.split('\n');
    const result = {};
    let currentKey = null;
    let currentContent = [];
    let isArray = false;
    
    for (let line of lines) {
      const trimmedLine = line.trim();
      
      // Check for heading (## or ###)
      if (trimmedLine.startsWith('## ') || trimmedLine.startsWith('### ')) {
        // Save previous section
        if (currentKey) {
          const snakeKey = currentKey.toLowerCase().replace(/\s+/g, '_');
          if (isArray) {
            result[snakeKey] = currentContent.filter(c => c);
          } else {
            result[snakeKey] = currentContent.join('\n').trim();
          }
        }
        
        // Start new section
        currentKey = trimmedLine.replace(/^#{2,3}\s+/, '');
        currentContent = [];
        isArray = false;
      } else if (trimmedLine.startsWith('• ') || trimmedLine.startsWith('- ')) {
        // Array item
        isArray = true;
        currentContent.push(trimmedLine.replace(/^[•\-]\s*/, ''));
      } else if (trimmedLine) {
        currentContent.push(trimmedLine);
      }
    }
    
    // Save last section
    if (currentKey) {
      const snakeKey = currentKey.toLowerCase().replace(/\s+/g, '_');
      if (isArray) {
        result[snakeKey] = currentContent.filter(c => c);
      } else {
        result[snakeKey] = currentContent.join('\n').trim();
      }
    }
    
    return result;
  };

  useEffect(() => {
    if (visible && initialData?.business_context) {
      const richText = objectToRichText(initialData.business_context);
      if (richText) {
        form.setFieldsValue({ businessContext: richText });
        setCharCount(richText.length);
        setEditorKey(prev => prev + 1); // Force editor to remount with new data
      }
    }
  }, [visible, initialData, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const parsedData = richTextToObject(values.businessContext);
      const oldContext = initialData?.business_context || {};
      
      // Check if anything changed
      const hasChanges = JSON.stringify(parsedData) !== JSON.stringify(oldContext);

      if (!hasChanges) {
        onClose();
        return;
      }

      // Send the parsed data as business_context
      const payload = {
        business_context: parsedData
      };

      const result = await onSave(cardKey, payload);
      if (result.success) {
        onClose();
      }
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setCharCount(0);
    onClose();
  };

  return (
    <SbModal
      title="Company Context"
      open={visible}
      onCancel={handleCancel}
      width={700}
      footer={[
        <SbButton key="cancel" buttonType="secondary" onClick={handleCancel}>
          Cancel
        </SbButton>,
        <SbButton key="save" buttonType="primary" loading={loading} onClick={handleSave}>
          Save
        </SbButton>
      ]}
    >
      <ModalHeaderText>Business Context</ModalHeaderText>
      
      <ModalDescription>
        This information is embedded into AI-generated JDs and outputs to ensure alignment with your business.
      </ModalDescription>

      <FormatGuideBox>
        <span className="guide-title">Format Guide:</span>
        <span className="guide-item">
          • Start each section with a heading: <code>Industry Focus</code>, <code>Key Goals</code>, or <code>Target Customer</code>
        </span>
        <span className="guide-item">
          • Follow each heading with the description on the next lines
        </span>
        <span className="guide-item">
          • Use line breaks to separate sections for better readability
        </span>
      </FormatGuideBox>

      <SbForm form={form} layout="vertical">
        <SbForm.Item
          name="businessContext"
          rules={[
            { required: true, message: 'Please enter business context' },
            { max: 4000, message: 'Maximum 4000 characters' }
          ]}
        >
          <CoreMarkdownEditor
            key={editorKey}
            initialMarkdown={form.getFieldValue('businessContext') || ''}
            onChange={(value) => {
              form.setFieldsValue({ businessContext: value });
              setCharCount(value?.length || 0);
            }}
            viewMode={VIEWMODE.EDIT}
          />
        </SbForm.Item>

        <CharacterCounter overLimit={charCount > 4000}>
          Total: {charCount} / 4000 characters
        </CharacterCounter>
      </SbForm>
    </SbModal>
  );
};

export default CompanyContextModal;
